import type { IExtensionResponse, INumberResponse } from '@/openapi-clients-generated/talk';

import type { IExtensionDetailedResponse } from './extension-types';

/**
 * Why an e911 location can't complete a 911 call: it was never registered with the carrier, so
 * there is no address to give a dispatcher, or no number holds it, so there is no caller ID for the
 * backend to resolve. Each fails silently at dial time, which is when nobody can afford to find out.
 */
export type E911Issue = 'noAddress' | 'noNumber';

export interface E911LocationGap {
    locationId: string;
    /** How many extensions name this location as where their phones physically sit. */
    extensionCount: number;
    issue: E911Issue;
}

/**
 * Whether ZynoTalk carries this number. A BYOC number (`accountId` set) routes 911 through the
 * tenant's own carrier, so its e911 registration is theirs to manage and none of our e911 checks
 * apply to it.
 */
export function isZynoTalkNumber(number: Pick<INumberResponse, 'accountId'>): boolean {
    return !number.accountId;
}

/**
 * Whether the tenant has any live ZynoTalk-carried number. Every e911 alert is gated on this: with
 * only BYOC numbers, a 911 call goes out through the tenant's carrier and our location and address
 * checks don't describe it.
 */
export function hasZynoTalkNumber(numbers: Pick<INumberResponse, 'accountId' | 'deletedAt'>[]): boolean {
    return numbers.some(n => !n.deletedAt && isZynoTalkNumber(n));
}

/** Shown in place of the e911 alerts when a tenant's numbers are all BYOC. */
export const BYOC_E911_NOTICE =
    'Your phone numbers are Bring-Your-Own-Carrier (BYOC) numbers, so ZynoTalk does not manage their e911 registration. It is your responsibility to ensure e911 is configured correctly with your carrier.';

/**
 * What is wrong with the location an extension is already assigned to, from the server's own
 * verdict. `null` means either that it works or that no location is set — the latter being a
 * different gap, reported separately (see `extensions-missing-e911`).
 *
 * `emergencyLocationStatus` is the service's answer to exactly this question: `active` when the
 * location is registered and a number holds it, `noMatchingNumber` when it is registered but bare,
 * and null when the location itself was never registered with the carrier. Read rather than
 * re-derived, so a list can't disagree with the backend that will actually place the call.
 */
export function getExtensionE911Issue(extension: Pick<IExtensionResponse, 'emergencyLocationId' | 'emergencyLocationStatus'>): E911Issue | null {
    if (!extension.emergencyLocationId || extension.emergencyLocationStatus === 'active') return null;
    return extension.emergencyLocationStatus === 'noMatchingNumber' ? 'noNumber' : 'noAddress';
}

/**
 * Whether a physical phone is attached to this extension — counting a phone it only sits on as an
 * *additional* extension (a second account or line key on someone else's endpoint), not just one
 * whose primary extension it is.
 *
 * An e911 question because only a phone can originate a 911 call that needs a registered address:
 * ZynoTalk softphones can't place one at all, so an extension with no hardware behind it has no e911
 * gap to report — and one that is a line key on a phone across the office does.
 *
 * Read from the server's counts rather than the endpoint list on purpose. `IEndpointResponse` (the
 * list) carries only the primary `extensionId`; additional extensions live on the per-endpoint
 * detail, so a list-derived check silently answers "no phone" for an extension that has one. The
 * counts come from a query that unions both sides, so they answer this in one field.
 */
export function hasAssignedPhone(extension: Pick<IExtensionDetailedResponse, 'endpointCountStandard' | 'endpointCountCordless'>): boolean {
    return extension.endpointCountStandard + extension.endpointCountCordless > 0;
}

/**
 * The locations extensions sit at that can't answer for them, one entry each.
 *
 * Grouped by location rather than listed per extension because that is the unit of the fix: one
 * address or one number resolves every extension standing at it.
 */
export function findE911LocationGaps(extensions: Pick<IExtensionResponse, 'emergencyLocationId' | 'emergencyLocationStatus'>[]): E911LocationGap[] {
    const gaps = new Map<string, E911LocationGap>();

    for (const extension of extensions) {
        const issue = getExtensionE911Issue(extension);
        if (!issue) continue;

        const locationId = extension.emergencyLocationId!;
        const existing = gaps.get(locationId);
        if (existing) existing.extensionCount++;
        else gaps.set(locationId, { locationId, extensionCount: 1, issue });
    }

    return [...gaps.values()];
}

/**
 * The same question for a location nothing is assigned to yet — what the editors ask while an admin
 * is choosing one, before anything is saved for the server to have an opinion about.
 *
 * Derived rather than read, necessarily: `emergencyLocationStatus` describes the location an
 * extension or number is already on, and the point here is the one being considered.
 */
export function getLocationE911Issue(input: {
    locationId: string;
    numbers: Pick<INumberResponse, 'emergencyLocationId' | 'deletedAt'>[];
    /** Locations carrying a carrier emergency address. */
    e911LocationIds: string[];
}): E911Issue | null {
    if (!input.e911LocationIds.includes(input.locationId)) return 'noAddress';
    return input.numbers.some(n => !n.deletedAt && n.emergencyLocationId === input.locationId) ? null : 'noNumber';
}
